/**
 * SSE Event Parser
 * Parses raw Server-Sent Events into typed event objects.
 */

import { logger } from './logger';

export interface TextDeltaEvent {
  type: 'textDelta';
  delta: string;
}

export interface ThinkingStartEvent {
  type: 'thinkingStart';
}

export interface ThinkingEndEvent {
  type: 'thinkingEnd';
}

export interface ArtifactUpdateEvent {
  type: 'artifactUpdate';
  name: string;
  fileType: string;
  ttl: number;
}

export interface ArtifactDeleteEvent {
  type: 'artifactDelete';
  name: string;
}

export interface SessionTerminatedEvent {
  type: 'sessionTerminated';
  reason: string;
}

export interface ErrorEvent {
  type: 'error';
  message: string;
}

export interface StatusUpdateEvent {
  type: 'statusUpdate';
  turnCount: number;
  maxTurns: number;
}

export interface PermissionAskedEvent {
  type: 'permission.asked';
  permissionId: string;
  operation: string;
  reason: string;
}

export interface QuestionAskedEvent {
  type: 'question.asked';
  questionId: string;
  question: string;
  quickReplies: string[];
}

export interface MessagePartUpdatedEvent {
  type: 'message.part.updated';
  sessionId: string;
  part: { type: 'text'; text: string };
}

export interface TurnWarningEvent {
  type: 'turn_warning';
  percentage: number;
  level: 'orange' | 'red';
  turnsRemaining: number;
  turnBoundary: number;
}

export interface DoneEvent {
  type: 'done';
  sessionId: string;
}

export interface OpenEvent {
  type: 'open';
}

export interface ClosedEvent {
  type: 'closed';
}

export interface SummaryGeneratingEvent {
  type: 'summaryGenerating';
  sessionId: string;
}

export interface SummaryCompleteEvent {
  type: 'summaryComplete';
  sessionId: string;
  filename: string;
  displayName: string;
}

export interface SummaryFailedEvent {
  type: 'summaryFailed';
  sessionId: string;
  error: string;
}

export interface VaultFileSkippedEvent {
  type: 'vault_file_skipped';
  filename: string;
  error: string;
}

export type SSEEvent =
  | TextDeltaEvent
  | ThinkingStartEvent
  | ThinkingEndEvent
  | ArtifactUpdateEvent
  | ArtifactDeleteEvent
  | SessionTerminatedEvent
  | ErrorEvent
  | StatusUpdateEvent
  | PermissionAskedEvent
  | QuestionAskedEvent
  | MessagePartUpdatedEvent
  | TurnWarningEvent
  | DoneEvent
  | OpenEvent
  | ClosedEvent
  | SummaryGeneratingEvent
  | SummaryCompleteEvent
  | SummaryFailedEvent
  | VaultFileSkippedEvent;

type SSEEventType = SSEEvent['type'];

const VALID_EVENT_TYPES: SSEEventType[] = [
  'textDelta',
  'thinkingStart',
  'thinkingEnd',
  'artifactUpdate',
  'artifactDelete',
  'sessionTerminated',
  'error',
  'statusUpdate',
  'permission.asked',
  'question.asked',
  'message.part.updated',
  'turn_warning',
  'done',
  'open',
  'closed',
  'summaryGenerating',
  'summaryComplete',
  'summaryFailed',
  'vault_file_skipped',
];

function isValidEventType(type: string): type is SSEEventType {
  return VALID_EVENT_TYPES.includes(type as SSEEventType);
}

function buildEvent(type: SSEEventType, payload: Record<string, unknown>): SSEEvent | null {
  try {
    switch (type) {
      case 'textDelta':
        return { type, delta: String(payload.delta ?? '') };
      case 'message.part.updated':
        return {
          type,
          sessionId: String(payload.sessionId ?? ''),
          part: { 
            type: 'text', 
            text: String((payload.part as any)?.text ?? '') 
          }
        };
      case 'turn_warning':
        return {
          type,
          percentage: Number(payload.percentage ?? 0),
          level: (payload.level as any) || 'orange',
          turnsRemaining: Number(payload.turnsRemaining ?? 0),
          turnBoundary: Number(payload.turnBoundary ?? 0),
        };
      case 'done':
        return { type, sessionId: String(payload.sessionId ?? '') };
      case 'sessionTerminated':
        return { type: 'sessionTerminated', reason: String(payload.reason ?? '') };
      case 'thinkingStart':
        return { type };
      case 'thinkingEnd':
        return { type };
      case 'artifactUpdate':
        return {
          type,
          name: String(payload.name ?? ''),
          fileType: String(payload.type ?? payload.fileType ?? ''),
          ttl: Number(payload.ttl ?? 0),
        };
      case 'artifactDelete':
        return { type, name: String(payload.name ?? '') };
      case 'error':
        return { type, message: String(payload.message ?? '') };
      case 'statusUpdate':
        return {
          type,
          turnCount: Number(payload.turnCount ?? 0),
          maxTurns: Number(payload.maxTurns ?? 0),
        };
      case 'permission.asked':
        return {
          type,
          permissionId: String(payload.permissionId ?? ''),
          operation: String(payload.operation ?? ''),
          reason: String(payload.reason ?? ''),
        };
      case 'question.asked':
        return {
          type,
          questionId: String(payload.questionId ?? ''),
          question: String(payload.question ?? ''),
          quickReplies: Array.isArray(payload.quick_replies) ? payload.quick_replies.map(String) : [],
        };
      case 'open':
        return { type };
      case 'closed':
        return { type };
      case 'summaryGenerating':
        return {
          type,
          sessionId: String(payload.sessionId ?? ''),
        };
      case 'summaryComplete':
        return {
          type,
          sessionId: String(payload.sessionId ?? ''),
          filename: String(payload.filename ?? ''),
          displayName: String(payload.displayName ?? ''),
        };
      case 'summaryFailed':
        return {
          type,
          sessionId: String(payload.sessionId ?? ''),
          error: String(payload.error ?? ''),
        };
      case 'vault_file_skipped':
        return {
          type,
          filename: String(payload.filename ?? ''),
          error: String(payload.error ?? ''),
        };
      default:
        return null;
    }
  } catch {
    return null;
  }
}

export function parseSSEPayload(type: string, payload: Record<string, unknown>): SSEEvent | null {
  if (!isValidEventType(type)) return null;
  return buildEvent(type as SSEEventType, payload);
}

/**
 * Parses a raw SSE event string into a typed SSEEvent object.
 * Returns null if the event is malformed or of an unknown type.
 * Note: Browser EventSource gives you pre-parsed data, so this is
 * primarily for tests or manual stream handling.
 */
export function parseSSEEvent(raw: string): SSEEvent | null {
  if (!raw || typeof raw !== 'string') {
    logger.error('SSE parse failed: empty or non-string input');
    return null;
  }

  const lines = raw.split('\n');
  let eventType = '';
  let dataPayload = '';

  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith('event:')) {
      eventType = trimmed.slice(6).trim();
    } else if (trimmed.startsWith('data:')) {
      dataPayload = trimmed.slice(5).trim();
    }
  }

  if (!eventType) {
    logger.error('SSE parse failed: missing event type', { raw: raw.slice(0, 200) });
    return null;
  }

  if (!isValidEventType(eventType)) {
    logger.warn('SSE parse failed: unknown event type', { eventType });
    return null;
  }

  let payload: Record<string, unknown> = {};
  if (dataPayload) {
    try {
      payload = JSON.parse(dataPayload) as Record<string, unknown>;
    } catch {
      logger.error('SSE parse failed: invalid JSON in data field', {
        eventType,
        dataPayload: dataPayload.slice(0, 200),
      });
      return null;
    }
  }

  const event = buildEvent(eventType, payload);
  if (!event) {
    logger.error('SSE parse failed: unable to build event', { eventType, payload });
    return null;
  }

  return event;
}
